/**
 * Le calendrier du planning (demande de Benjamin, 2026-10-30) : un clic sur le libellé de la période l'ouvre. L'année se change
 * par ‹ ›, un mois se choisit dans la grille des douze, puis un jour ou un numéro de semaine ouvre cette période dans le planning
 * (en vue Mois, choisir un mois l'ouvre directement). « Aller au » prend une date tapée. Échap ou un clic à côté le referme.
 *
 * Les cases sont des `button.secondaire` : leur survol est la règle déjà vérifiée des boutons secondaires (texte lisible) — aucune
 * règle de survol nouvelle. Le jour choisi est rendu à la page, qui en tire le début de la période.
 */
import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';

import {
  JOURS_CALENDRIER,
  MOIS_CALENDRIER,
  ajouterMois,
  lireDateTapee,
  premierDuMois,
  semainesDuMois,
  type VuePlanning,
} from '../domaine/planning-calendrier';
import { jourFr } from '../domaine/planning';

export function CalendrierPlanning({
  vue,
  debut,
  fin,
  aujourdhui,
  libelle,
  onAller,
}: {
  vue: VuePlanning;
  /** Premier et dernier jour de la période affichée (pour la marquer dans le calendrier). */
  debut: string | null;
  fin: string | null;
  aujourdhui: string;
  libelle: string;
  /** Un jour choisi : la page ouvre la période qui le contient. */
  onAller: (jour: string) => void;
}) {
  const [ouvert, setOuvert] = useState(false);
  const [mois, setMois] = useState(premierDuMois(debut ?? aujourdhui));
  const [tape, setTape] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const racine = useRef<HTMLDivElement>(null);

  // À l'ouverture, le calendrier montre le mois de la période affichée.
  useEffect(() => {
    if (ouvert) {
      setMois(premierDuMois(debut ?? aujourdhui));
      setTape('');
      setErreur(null);
    }
  }, [ouvert, debut, aujourdhui]);

  useEffect(() => {
    if (!ouvert) return;
    const clic = (e: MouseEvent) => {
      if (racine.current && !racine.current.contains(e.target as Node)) setOuvert(false);
    };
    const touche = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOuvert(false);
    };
    document.addEventListener('mousedown', clic);
    document.addEventListener('keydown', touche);
    return () => {
      document.removeEventListener('mousedown', clic);
      document.removeEventListener('keydown', touche);
    };
  }, [ouvert]);

  const aller = (jour: string) => {
    onAller(jour);
    setOuvert(false);
  };

  const annee = mois.slice(0, 4);
  const allerAuJour = (e: FormEvent) => {
    e.preventDefault();
    const jour = lireDateTapee(tape);
    if (jour === null) {
      setErreur('Date illisible : tapez par exemple 01/01/2026.');
      return;
    }
    aller(jour);
  };
  const dansLaPeriode = (jour: string) => debut !== null && fin !== null && jour >= debut && jour <= fin;

  return (
    <div className="calendrier-planning" ref={racine}>
      <button type="button" className="secondaire calendrier-libelle" aria-expanded={ouvert} aria-haspopup="dialog" onClick={() => setOuvert((o) => !o)}>
        {libelle} ▾
      </button>
      {ouvert && (
        <div className="calendrier-planning-panneau" role="dialog" aria-label="Choisir une date">
          <div className="calendrier-annee">
            <button type="button" className="secondaire discret" aria-label="Année précédente" onClick={() => setMois(ajouterMois(mois, -12))}>
              ‹
            </button>
            <strong>{annee}</strong>
            <button type="button" className="secondaire discret" aria-label="Année suivante" onClick={() => setMois(ajouterMois(mois, 12))}>
              ›
            </button>
          </div>
          <div className="calendrier-mois">
            {MOIS_CALENDRIER.map((m, i) => {
              const premier = `${annee}-${String(i + 1).padStart(2, '0')}-01`;
              return (
                <button
                  key={m}
                  type="button"
                  className={`secondaire discret${premier === mois ? ' actif' : ''}`}
                  aria-pressed={premier === mois}
                  onClick={() => (vue === 'mois' ? aller(premier) : setMois(premier))}
                >
                  {m}
                </button>
              );
            })}
          </div>
          <div className="calendrier-jours" role="grid" aria-label={`Jours du mois`}>
            <span className="calendrier-tete">Sem.</span>
            {JOURS_CALENDRIER.map((j, i) => (
              <span key={i} className="calendrier-tete">
                {j}
              </span>
            ))}
            {semainesDuMois(mois).map((s) => (
              <SemaineDuCalendrier key={s.lundi} semaine={s} vue={vue} aujourdhui={aujourdhui} dansLaPeriode={dansLaPeriode} onAller={aller} />
            ))}
          </div>
          <form className="calendrier-aller" onSubmit={allerAuJour}>
            <label htmlFor="calendrier-aller-au">Aller au</label>
            <input
              id="calendrier-aller-au"
              value={tape}
              placeholder={jourFr(aujourdhui)}
              onChange={(e) => {
                setTape(e.target.value);
                setErreur(null);
              }}
            />
            <button type="submit" className="secondaire discret">
              OK
            </button>
          </form>
          {erreur && <div className="message erreur">{erreur}</div>}
        </div>
      )}
    </div>
  );
}

function SemaineDuCalendrier({
  semaine,
  vue,
  aujourdhui,
  dansLaPeriode,
  onAller,
}: {
  semaine: ReturnType<typeof semainesDuMois>[number];
  vue: VuePlanning;
  aujourdhui: string;
  dansLaPeriode: (jour: string) => boolean;
  onAller: (jour: string) => void;
}) {
  return (
    <>
      <button
        type="button"
        className={`secondaire discret calendrier-numero${vue === 'semaine' && dansLaPeriode(semaine.lundi) ? ' actif' : ''}`}
        title={`Ouvrir la semaine ${semaine.numero}`}
        onClick={() => onAller(semaine.lundi)}
      >
        {semaine.numero}
      </button>
      {semaine.jours.map(({ jour, dansLeMois }) => (
        <button
          key={jour}
          type="button"
          className={`secondaire discret calendrier-jour${dansLeMois ? '' : ' hors-mois'}${dansLaPeriode(jour) ? ' actif' : ''}${jour === aujourdhui ? ' aujourdhui' : ''}`}
          title={jourFr(jour)}
          onClick={() => onAller(jour)}
        >
          {Number(jour.slice(8, 10))}
        </button>
      ))}
    </>
  );
}
